import classNames from "classnames";
import isEmpty from "lodash/fp/isEmpty";
import omit from "lodash/fp/omit";
import { useContext, useState } from "react";

import { Button } from "@rio-cloud/rio-uikit";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, { type SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles, { type TableViewTogglesViewType } from "@rio-cloud/rio-uikit/TableViewToggles";
import { IOnGoingTrip } from "@types";
import { formatDateBr, formatDateBrUtc } from "@utils";
import moment from "moment";
import { AuthContext, TDrivers, TFollowers } from "src/providers/AuthProvider";
import Avatar from "../Avatar/indext";
import ChipStatus from "../ChipStatus";
import PaginatorSize, { ConfigPaginator } from "../PaginatorSize";
import TableChip from "../TableChip";
import TableRowLoading from "../TableRowLoading";

const defaultColumnOrder = [
  "name",
  "vehicle",
  "dateStarted",
  "status",
  "statusTrip",
  "routeSuggestion",
  "duration",
  "destination",
  "action",
];

type ColumnLabel = {
  [key: string]: string;
};
const columnLabels: ColumnLabel = {
  name: "Motorista",
  vehicle: "Veículo",
  dateStarted: "Agendamento",
  status: "Status da rota",
  statusTrip: "Status de chegada",
  duration: "HEC",
  routeSuggestion: "Sugestão de rota",
  destination: "Destino",
  action: "",
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 270,
    defaultWidth: 270,
    maxWidth: 270,
  },
  duration: {
    width: 160,
    defaultWidth: 160,
    maxWidth: 160,
  },
};

const getSortDir = (sortDir: SortDirectionType, sortBy: string, previousSortBy: string) => {
  if (sortBy === previousSortBy) {
    return sortDir === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
  }
  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  pages: number;
  totalTrips?: number;
  size: number;
  totalSize: number;
  routes: IOnGoingTrip[];
  searchValue: string;
  page: number;
  followers?: TFollowers;
  pageSize: number;
  handleSearchValueChange: (text: string) => void;
  handlePage: (config: ConfigPaginator) => void;
  handleResetPage: () => void;
  handleSort: (dir: string, newSort: string) => void;
  handleSelect: (s: IOnGoingTrip) => void;
  selected?: string;
  handleDetails: (s: IOnGoingTrip) => void;
  suggestedRoutes: any[];
  rioDrivers: TDrivers;
  handleAcceptTrip: (tripId: string, accepted: boolean) => Promise<void>;
  isLoading: boolean;
  isLoadingMore?: boolean;
};

const TableOnGoingTrip = (props: Props) => {
  const { viewType: externalViewType } = props;
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState<SortDirectionType>(SortDirection.ASCENDING);
  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(externalViewType || TableViewToggles.VIEW_TYPE_TABLE);

  const handleToggleTableSettingsDialog = () => setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) => setViewType(newViewType);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  // For immediate effect
  const handleColumnDetailsChange = (column: string, newColumnDetails: ColumnDetails) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");
    if (newSortBy) {
      handleCardSortChange(newSortBy, getSortDir(sortDir, newSortBy, sortBy));
      props.handleSort(sortDir, newSortBy);
    }
  };

  const handleCardSortChange = (newSortBy: string, newSortDir: SortDirectionType) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
  };

  // May be extracted as a dedicated component but for demo purpose it's shown here
  const renderTableHead = (column: string, label: string, sortByColumn: string, sortDirection: SortDirectionType) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? (
            <SortArrows direction={sortDirection} />
          ) : column !== "action" ? (
            <SortArrows />
          ) : null}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
          minWidth: columnDetails.width,
          width: columnDetails.width,
        }
      : {};

    return <col key={column} style={style} />;
  };

  // filter for hidden columns
  const columns = columnOrder.filter((name) => !hiddenColumns.includes(name));

  // filter data to omit hidden columns
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = props.routes.map((vehicle) => ({
    ...withoutHidden(vehicle),
  })) as IOnGoingTrip[];

  // in case a search value is given, filter the data accordingly
  const searchResult = filteredRows;

  // Sort rows according to the sortBy and sortDir settings
  //const rows = searchResult;
  const rows = props.routes;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    props?.routes?.length > 0 && "table-hover",
    "cursor-pointer",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD && "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS && "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = columns.map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy,
      disabled: false,
    };
  });

  const [loadingTripId, setLoadingTripId] = useState<string | null>(null);

  const handleActionTrip = async (tripId: string, accepted: boolean, row: IOnGoingTrip) => {
    setLoadingTripId(tripId);
    try {
      await props.handleAcceptTrip(tripId, accepted);
      props.handleDetails(row);
    } finally {
      setLoadingTripId(null);
    }
  };

  const { getDetailUser, finderDriverFleet } = useContext(AuthContext);

  return (
    <div id="table-trips">
      <TableToolbar>
        <div className="table-toolbar-container">
          <div className="table-toolbar-group-left">
            <div className="table-toolbar-column">
              <div className="btn-toolbar table-btn-toolbar">
                <TableSearch
                  className="width-400"
                  value={props.searchValue}
                  onChange={props.handleSearchValueChange}
                  placeholder="Buscar por motorista, veículo e destino"
                />
              </div>
            </div>
          </div>
          <div className="table-toolbar-group-right">
            <div className="table-toolbar-column">
              <TableViewToggles
                initialViewType={viewType}
                onViewTypeChange={handleViewTypeChange}
                tableViewTooltipContent="Table view"
                singleCardViewTooltipContent="List view"
                multiCardsViewTooltipContent="Cards view"
              />
            </div>
          </div>
          {showTableSettingsDialog && (
            <TableSettingsDialog
              show={showTableSettingsDialog}
              title="Table settings"
              onHide={handleToggleTableSettingsDialog}
              onColumnChange={handleColumnChange}
              defaultColumnOrder={defaultColumnOrder}
              defaultHiddenColumns={[]}
              columnOrder={columnOrder}
              hiddenColumns={hiddenColumns}
              columnLabels={columnLabels}
              disabledColumns={[]}
              closeButtonText="Close"
              resetButtonText="Reset to default"
              searchPlaceholder="Search by column name"
              notFoundMessage="No column found for this search value"
              columnsDetails={columnsDetails}
              autoLabel="Auto"
              onColumnDetailsChange={handleColumnDetailsChange}
              onSearchChange={(val: string) => console.log(val)}
              immediateChange
            />
          )}
        </div>
      </TableToolbar>

      {sortBy && !isViewTypeTable && (
        <TableCardsSorting
          selectOptions={cardSortingSelectOptions}
          sortName={sortBy}
          sortOrder={sortDir}
          onSortChange={handleCardSortChange}
        />
      )}

      <div>
        {isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState headline="Sem resultados" message="Por favor refaça a busca." />
        )}

        <table className={tableClassNames}>
          <colgroup>{columns.map((column) => renderTableCaption(column, columnsDetails[column]))}</colgroup>

          <thead>
            <tr>{columns.map((column) => renderTableHead(column, columnLabels[column], sortBy, sortDir))}</tr>
          </thead>

          <tbody>
            {props.isLoading ? <TableRowLoading cols={10} rows={10} /> : null}

            {!props.isLoading && isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Sem resultados"
                    message="Por favor refaça a busca."
                  />
                </td>
              </tr>
            )}

            {!props.isLoading &&
              rows?.map((row: IOnGoingTrip, index: number) => {
                const a = getDetailUser?.(row.profileId);
                const finder = finderDriverFleet?.(row?.driverId);
                return (
                  <tr key={index} className={row.routeId === props.selected ? "active" : ""}>
                    <td key={"Motorista"} data-field={"Motorista"} onClick={() => props?.handleSelect(row)}>
                      <div className="display-flex flex-direction-row align-items-center">
                        <Avatar url={a?.metadata?.imageProfile?.fileUrl} />

                        <div className="padding-x-15">
                          <span className="text-bold text-size-14 text-capitalize">{row.driverName}</span>
                          <div className="margin-top-5">
                            <TableChip disabledDriver={!finder} disabledProfile={!row?.profileId} user={a} />
                          </div>
                        </div>
                      </div>
                    </td>

                    <td key={"Veículo"} data-field={"Veículo"} onClick={() => props?.handleSelect(row)}>
                      <span className="text-size-14 text-norma text-color-darkest">{row.assetName}</span>
                    </td>

                    <td key={"dateStarted"} data-field={"dateStarted"} onClick={() => props?.handleSelect(row)}>
                      <div className="width-150">{formatDateBr(row.scheduledDateTime)}</div>
                    </td>

                    <td key={"Status"} data-field={"Status"} onClick={() => props?.handleSelect(row)}>
                      <div>
                        <ChipStatus
                          status={
                            row.tripStatusNow === "NOTSTARTED" && !row.polyline?.length
                              ? "NOTSTARTED"
                              : row.routeStatusNow
                          }
                        />
                      </div>
                    </td>

                    <td key={"StatusTrip"} data-field={"StatusTrip"} onClick={() => props?.handleSelect(row)}>
                      <div>
                        <ChipStatus
                          status={
                            row.tripStatusNow === "NOTSTARTED" && row?.polyline && row?.polyline?.length > 0
                              ? "PAUSED"
                              : row.tripStatusNow === "NOTSTARTED" && !row?.polyline?.length
                                ? "NOTSTARTED"
                                : row.tripStatusNow === "URGENT_ALERT"
                                  ? "STARTED"
                                  : row.tripStatusNow
                          }
                        />
                      </div>
                    </td>

                    <td
                      key={"routeSuggestion"}
                      data-field={"routeSuggestion"}
                      onClick={() => props?.handleSelect(row)}
                      className="width-auto"
                    >
                      {props.suggestedRoutes.find((f) => f.tripId === row.tripId)?.data ? (
                        <div className="display-flex flex-column gap-10 align-items-center text-center">
                          <Button
                            className="width-120 height-36"
                            onClick={(e) => {
                              e.stopPropagation();
                              props?.handleDetails(row);
                            }}
                          >
                            <span className="text-normal">Ver sugestão</span>
                          </Button>
                          {row?.suggest && (
                            <span className="text-size-14 text-normal text-color-darkest">{row?.suggest}</span>
                          )}
                        </div>
                      ) : (
                        <div className="display-flex justify-content-center">-</div>
                      )}
                    </td>

                    <td
                      key={"Duração"}
                      data-field={"Duração"}
                      className="text-center"
                      onClick={() => props?.handleSelect(row)}
                    >
                      <span className="text-size-14 text-normal text-color-darkest">
                        {formatDateBrUtc(moment?.(row.eta, "DD/MM/YYYY HH:mm")?.format("YYYY-MM-DDTHH:mm:ss"))}
                      </span>
                      {row.hec48hrs && (row.tripStatusNow === "STARTED" || row.tripStatusNow === "PAUSED") && (
                        <>
                          <br />
                          <div className="display-flex bg-danger justify-content-center align-items-center rounded-extra-large padding-5 margin-top-2">
                            <span className="rioglyph rioglyph-time-alt always-text-color-white text-bold" />
                            <span className="margin-left-5 always-text-color-white text-medium"> HEC 48 horas +</span>
                          </div>
                        </>
                      )}
                    </td>

                    <td key={"Destino"} data-field={"Destino"} onClick={() => props?.handleSelect(row)}>
                      <span className="text-size-14 text-normal text-color-darkest">{row.destinyName}</span>
                    </td>

                    <td key={"Action"} data-field={"Action"}>
                      <div className="display-flex width-120">
                        <Button className="width-120 height-36" onClick={() => props.handleDetails(row)}>
                          <span className="text-normal">Ver Detalhes</span>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {!props.isLoading ? (
        <PaginatorSize
          size={props.size}
          totalSize={props.totalSize}
          handleLoadMore={props.handlePage}
          handleReset={props.handleResetPage}
          isLoading={props.isLoadingMore}
        />
      ) : null}
    </div>
  );
};

export default TableOnGoingTrip;
